import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { PageHeader } from "@/client/components/page-header";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { allowedIpsApi } from "@/client/features/allowed-ips/allowed-ips-api";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { ApiError } from "@/client/lib/api";
import { NotFound } from "@/client/pages/not-found";
import { parseAllowedIp } from "@/shared/allowed-ip";

const textareaClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 min-h-24 w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

/** Split a free-text ranges field into individual tokens (comma / whitespace). */
function splitRanges(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

const requestSchema = z
  .object({
    ranges: z.string().min(1, "Enter at least one IP address or range."),
    note: z.string().trim().max(2000, "Keep notes under 2000 characters.").optional(),
  })
  .superRefine((val, ctx) => {
    const tokens = splitRanges(val.ranges);
    if (tokens.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ranges"],
        message: "Enter at least one IP address or range.",
      });
      return;
    }
    for (const token of tokens) {
      const parsed = parseAllowedIp(token);
      if (!parsed.ok) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ranges"],
          message: `${token}: ${parsed.reason}`,
        });
      }
    }
  });

type RequestValues = z.infer<typeof requestSchema>;

// A tenant's source-IP allowlist is provisioned by Mantle (PE-957): firewall /
// edge-router whitelisting isn't automated end-to-end yet, so the portal
// collects the request and files it as a support ticket rather than changing
// anything directly.
function RequestDialog() {
  const [open, setOpen] = useState(false);
  const form = useForm<RequestValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: { ranges: "", note: "" },
  });

  const onSubmit = async (values: RequestValues) => {
    try {
      const ticket = await allowedIpsApi.request({
        ranges: splitRanges(values.ranges),
        note: values.note?.trim() || undefined,
      });
      toast.success(
        `Network access request ${ticket.identifier} submitted. Track it under Support.`,
      );
      form.reset({ ranges: "", note: "" });
      setOpen(false);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Unable to submit the request. Please try again.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Request network access</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request network access</DialogTitle>
          <DialogDescription>
            List the source IP addresses or ranges your team connects from. Firewall access is set
            up by the Mantle team, so we'll open a support request you can track. To remove existing
            access, describe it in the notes.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="ranges"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>IP addresses or ranges</FormLabel>
                  <FormControl>
                    <textarea
                      className={`${textareaClass} font-mono`}
                      placeholder={"198.51.100.7\n203.0.113.0/24"}
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    One per line or comma-separated. A single IP (stored as a /32) or a CIDR range;
                    IPv4 must be /24 or smaller and IPv6 /48 or smaller.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (optional)</FormLabel>
                  <FormControl>
                    <textarea
                      className={textareaClass}
                      placeholder="Anything that helps us set this up, or ranges you'd like removed."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Submitting…" : "Submit request"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export function AllowedIpsPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.kind === "customer" && user.tenantRole === "tenant_admin";
  const networkAccessEnabled = useAuthStore((s) => s.features.networkAccess);
  const ticketsEnabled = useAuthStore((s) => s.features.tickets);
  // Requests are filed as support tickets and view-as sessions are read-only, so
  // only offer the action to a tenant admin when it can actually succeed.
  const impersonating = useAuthStore((s) => s.impersonation != null);
  const deactivated = useAuthStore((s) => s.deactivated);
  const canRequest = isAdmin && ticketsEnabled && !impersonating && !deactivated;

  // The nav hides Network access for unentitled tenants and the API 404s the
  // whole /allowed-ips subtree; match that here so a direct URL visit doesn't
  // render the page.
  if (!networkAccessEnabled) {
    return <NotFound />;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Network access"
        description="Control which source IP addresses and ranges can reach your Mantle infrastructure."
      >
        {canRequest ? <RequestDialog /> : null}
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>How it works</CardTitle>
          <CardDescription>
            Firewall / edge-router whitelisting is provisioned by the Mantle team. Tell us the
            source IPs and ranges your team connects from and we'll set up access and follow up on
            the request.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          {canRequest ? (
            <p>
              Submit a request above and we'll follow up. You can track it on the{" "}
              <Link to="/tickets" className="text-foreground underline underline-offset-4">
                Support
              </Link>{" "}
              page.
            </p>
          ) : impersonating ? (
            <p>Network access requests are disabled while viewing as a tenant.</p>
          ) : deactivated ? (
            <p>
              Network access requests are disabled because this account is deactivated. Contact your
              Mantle account team to reactivate.
            </p>
          ) : !isAdmin ? (
            <p>
              Only tenant admins can request network access changes. Contact an admin on your team
              to make changes.
            </p>
          ) : (
            <p>
              Network access requests are handled through Support, which isn't available right now.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
